<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_TRANSACTION_CALLOVER_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
            :disabled="isDisabled"
          >
            <b-dropdown-item @click="csvExport()">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport()">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="transactionCallOverTableRef"
      :items="cachedTransactionCallOverList ? cachedTransactionCallOverList.data : reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >
      <validation-observer ref="transactionCallOverRules">
        <b-form>
          <b-row class="match-height">
            <!-- Account Number -->
            <b-col md="6">
              <InputField
                id="account-number"
                v-model="filter.accountNumber"
                :label="$t('CASA Number')"
                :placeholder="$t('Enter CASA Number')"
                type="text"
              />
            </b-col>
            <!-- Account Number -->
            <!-- GL Code -->
            <b-col md="6">
              <InputField
                id="gl-code"
                v-model="filter.glCode"
                :label="$t('GL Code')"
                :placeholder="$t('Enter GL Code')"
                type="text"
              />
            </b-col>
            <!--  GL Code -->
          </b-row>

          <b-row class="match-height">
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="filter.branchCode"
                :label="$t('Branch')"
                :placeholder="$t('Select')"
                :reduce="option => option.value"
                :options="branchOptions"
                item="label"
                @input="getBranchValue(filter.branchCode)"
              />
            </b-col>
            <!-- Branch -->
            <!-- Instrument Number -->
            <b-col md="6">
              <InputField
                id="instrument-number"
                v-model="filter.instrumentNumber"
                :label="$t('Instrument Number')"
                :placeholder="$t('Enter Instrument Number')"
                type="text"
                for="instrument-number"
              />
            </b-col>
            <!-- Instrument Number -->
          </b-row>

          <b-row class="match-height">
            <!-- Financial Date From -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-from"
                v-model="filter.financialDateFrom"
                :min-date="new Date()"
                :label="$t('Financial Date (From)')"
                :error-name="$t('Financial Date From')"
                :config="datePickerConfig"
                :placeholder="$t('Date From')"
              />
            </b-col>
            <!-- Financial Date From -->
            <!-- Financial Date To -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-to"
                v-model="filter.financialDateTo"
                :min-date="new Date()"
                :label="$t('Financial Date (To)')"
                :error-name="$t('Financial Date To')"
                :config="datePickerConfig"
                :placeholder="$t('Date To')"
              />
            </b-col>
            <!-- Financial Date To -->
            <!-- Start Date -->
            <b-col md="6">
              <DatePickerField
                id="transaction-date-from"
                v-model="filter.startDate"
                :min-date="new Date()"
                :label="$t('Transaction Date (From)')"
                :error-name="$t('Transaction Date From')"
                :config="datePickerConfig"
                :placeholder="$t('Transaction Date From')"
              />
            </b-col>
            <!-- Start Date -->
            <!-- End Date -->
            <b-col md="6">
              <DatePickerField
                id="transaction-date-to"
                v-model="filter.endDate"
                :min-date="new Date()"
                :label="$t('Transaction Date (To)')"
                :error-name="$t('Transaction Date To')"
                :config="datePickerConfig"
                :placeholder="$t('Transaction Date To')"
              />
            </b-col>
            <!-- End Date -->
          </b-row>

          <b-row class="match-height">
            <!-- Sort Code -->
            <b-col md="6">
              <InputField
                id="entry-code"
                v-model="filter.entryCode"
                :label="$t('Sort Code')"
                :placeholder="$t('Enter Sort Code')"
                type="text"
                for="entry-code"
              />
            </b-col>
            <!-- Sort Code -->
            <!-- Posting Reference Number -->
            <b-col md="6">
              <InputField
                id="posting-reference-number"
                v-model="filter.postingReferenceNumber"
                :label="$t('Posting Reference Number')"
                :placeholder="$t('Enter Reference Number')"
                type="text"
                for="posting-reference-number"
              />
            </b-col>
            <!-- Posting Reference Number -->
            <!-- Posting Initiated By -->
            <b-col md="6">
              <SelectField
                id="posting-initiated-by"
                v-model="filter.initiatedBy"
                :label="$t('Initiated By')"
                :placeholder="$t('Select Initiated By')"
                :reduce="option => option.value"
                :options="allUsersOptions"
                item="label"
              />
            </b-col>
            <!-- Posting Initiated By -->
            <!-- Approved By -->
            <b-col md="6">
              <SelectField
                id="posting-approved-by"
                v-model="filter.approvedBy"
                :label="$t('Approved By')"
                :placeholder="$t('Select Approved By')"
                :reduce="option => option.value"
                :options="allUsersOptions"
                item="label"
              />
            </b-col>
            <!-- Approved By -->
          </b-row>
        </b-form>
      </validation-observer>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BSpinner,
  BModal,
  BDropdown,
  BDropdownItem,
  BForm,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { mapGetters, mapActions } from 'vuex'
import { formatAmount, toBalance, toFormatAmount } from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import Button from '../../../components/button/Button.vue'
import toNumber from '../../../../helpers/toNumber'
import { toDateWithCustomTime, toCustomDate } from '../../../../helpers/formatDate'
import { reportDataDetails } from '../../../../helpers/data'
import { reportPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    TableEntries,
    InputField,
    SelectField,
    BForm,
    BModal,
    BDropdown,
    BDropdownItem,
    DatePickerField,
    ValidationObserver,
    // vSelect,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toBalance,
      query: '',
      toFormatAmount,
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isBusy: false,
      isDisabled: false,
      isLoading: false,
      required,
      modalTitle: this.$t('Transaction CallOver Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'financialDate', sortable: true, label: 'Financial Date' },
        { key: 'transactionDate', sortable: true, label: 'Transaction Date' },
        { key: 'accountNumber', sortable: true, label: 'GL/Account Number' },
        { key: 'accountName', sortable: true, label: 'GL/Account Name' },
        { key: 'debit', sortable: true, label: 'Debit' },
        { key: 'credit', sortable: true, label: 'Credit' },
        { key: 'postingReferenceNumber', sortable: true, label: 'Posting Ref No.' },
        { key: 'instrumentNumber', sortable: true, label: 'Instrument No.' },
        { key: 'entryCode', sortable: true, label: 'Entry Code' },
        { key: 'branch', sortable: true, label: 'Branch Name' },
        { key: 'narration', sortable: true, label: 'Narration' },
        { key: 'initiatedBy', sortable: true, label: 'Initated By' },
        { key: 'approvedBy', sortable: true, label: 'Approved By' },
      ],
      filter: {
        accountNumber: '',
        postingReferenceNumber: '',
        financialDateFrom: null,
        financialDateTo: null,
        startDate: null,
        endDate: null,
        instrumentNumber: '',
        entryCode: '',
        glCode: '',
        branchCode: '',
        initiatedBy: '',
        approvedBy: '',
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
      },
      allUsersOptions: [],
      reportData: [],
      allSearchedData: [],
      branchRefCode: '',
      selected: '',
      reportInfo: reportDataDetails,
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
      userDetails: 'auth/getCurrentUser',
    }),
    ...mapGetters('tableCache', ['getTableCache']),
    cacheKey() {
      // Use page + perPage + filter as cacheKey
      return JSON.stringify({ page: this.currentPage, perPage: this.perPage, filter: this.filter })
    },
    cachedTransactionCallOverList() {
      return this.getTableCache('transaction-call-over-report', this.cacheKey)
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({
        ...header,
        label: header.label ? this.$t(header.label) : header.key,
      }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.serveCacheAndFetchTransactionCallOverReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.serveCacheAndFetchTransactionCallOverReport()
    },
  },
  mounted() {
    this.serveCacheAndFetchTransactionCallOverReport()
    this.fetchAllUsersOptions()
  },
  methods: {
    ...mapActions({
      getCallOvers: 'reports/getTransactionReports',
      downloadCallOvers: 'reports/downloadTransactionReports',
      getBranchByCode: 'operations/getBranchByCode',
      getUsers: 'operations/getAllUsers',
      downloadCallOversReports: 'reports/downloadTransactionCallOversReports',
    }),
    ...mapActions('tableCache', ['setTableCache', 'markTableCacheStale', 'clearTableCache']),
    async serveCacheAndFetchTransactionCallOverReport() {
      // Serve cache if available
      const { cacheKey } = this
      const cache = this.getTableCache('transaction-call-over-report', cacheKey)
      if (cache && cache.data) {
        this.reportData = cache.data
        this.totalCount = cache.totalCount
        this.isBusy = false
        this.fetchTransactionCallOverReport(true)
      } else {
        // No cache, show spinner and fetch
        this.isBusy = true
        await this.fetchTransactionCallOverReport(false)
      }
    },

    resetFilters() {
      this.filter = { ...null }
      this.markTableCacheStale('transaction-call-over-report')
    },
    resetFilter() {
      this.filter = { ...null }
      this.markTableCacheStale('transaction-call-over-report')
      this.fetchTransactionCallOverReport(true)
    },
    async fetchAllUsersOptions() {
      try {
        this.isBusy = true
        const formData = {
          status: 'Active',
        }
        const response = await this.getUsers({
          formData,
          permission: reportPermissions.TRANSACTION_CALLOVER_REPORT,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.isBusy = false
          this.allUsersOptions = data.data.map(item => ({
            label: `${item?.firstname ?? ''} ${item?.lastname ?? ''} ${item?.othernames ?? ''}`,
            value: item.email,
          }))
          this.total = data.recordCount
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async getBranchValue(dataValue) {
      if (dataValue !== null) {
        try {
          const response = await this.getBranchByCode(dataValue)
          const { status, data } = response
          if (status === 200 && data) {
            this.branchRefCode = data
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.isBusy = false
        }
      }
    },
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.transactionCallOverRules.validate().then(async success => {
        if (success) {
          this.markTableCacheStale('transaction-call-over-report')
          this.fetchTransactionCallOverReport(false)
        }
      })
    },
    async fetchTransactionCallOverReport(isBackground = false) {
      if (!isBackground) {
        this.isBusy = true
      }
      try {
        this.hideModal('modal-filter')
        const response = await this.getCallOvers({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: reportPermissions.TRANSACTION_CALLOVER_REPORT,
          route: 'transactionCallOverReport',
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          const cache = this.cachedTransactionCallOverList
          const isDifferent = !cache || JSON.stringify(cache.data) !== JSON.stringify(data.data)
          if (isDifferent) {
            this.reportData = data.data.map(item => ({
              ...item,
              debit: toBalance(item.debit),
              credit: toBalance(item.credit),
              financialDate: this.formatCustomDate(item.financialDate),
              transactionDate: this.formatDateTime(item.transactionDate),
            }))
            this.totalCount = data.recordCount
            // If on page 1 and data changed, mark all branch cache as stale
            if (this.currentPage === 1) {
              this.markTableCacheStale('transaction-call-over-report')
            }
          }
          // Always update cache
          this.setTableCache({
            tableKey: 'transaction-call-over-report',
            cacheKey: this.cacheKey,
            payload: {
              data: data.data.map(item => ({
                ...item,
                debit: toBalance(item.debit),
                credit: toBalance(item.credit),
                financialDate: this.formatCustomDate(item.financialDate),
                transactionDate: this.formatDateTime(item.transactionDate),
              })),
              totalCount: data.recordCount,
              lastFetched: Date.now(),
              stale: false,
            },
          })
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadCallOversReports({
          search: this.filter,
          fileExportFormat: 1,
          permission: reportPermissions.EXPORT_TRANSACTION_CALLOVER_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: 'Your download is in progress and might take a little longer. Go to your ',
            link: {
              text: 'Download Manager',
              route: { name: 'download-manager' },
            },
            afterLinkText: ' to access the completed file.',
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadCallOversReports({
          search: this.filter,
          fileExportFormat: 0,
          permission: reportPermissions.EXPORT_TRANSACTION_CALLOVER_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: 'Your download is in progress and might take a little longer. Go to your ',
            link: {
              text: 'Download Manager',
              route: { name: 'download-manager' },
            },
            afterLinkText: ' to access the completed file.',
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    toAmount: formatAmount,
    formatNumber: toNumber,
    formatDateTime: toDateWithCustomTime,
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
